import { Signal, inject } from '@angular/core';
import { ToSignalOptions, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable, Subscribable, filter, map, switchMap } from 'rxjs';

export const IMG_PLACEHOLDER = '/assets/placeholder.jpg';

export function handleImageError(event: Event) {
  const imgElement = event.target as HTMLImageElement;
  if (imgElement.src !== IMG_PLACEHOLDER) {
    imgElement.src = IMG_PLACEHOLDER;
  }
}

export function rxSignal<T, const U extends T>(source: Observable<T> | Subscribable<T>, initialValue: U) {
  return toSignal(source, { initialValue });
}

export function signalAsync<S, O>(
  signal: Signal<S>,
  observable: (value: S) => Observable<O>,
  options: ToSignalOptions<O> = {}
) {
  return toSignal(
    toObservable(signal, options).pipe(switchMap(value => observable(value))),
    options as any
  ) as Signal<O>;
}

export type RouteParams = Record<string, string | undefined>;

export function getRouteParams(route: ActivatedRoute): RouteParams {
  let params = route.snapshot.params;
  let parent = route.parent;
  while (parent) {
    params = { ...parent.snapshot.params, ...params };
    parent = parent.parent;
  }
  return params;
}

export function injectRouteParams() {
  const route = inject(ActivatedRoute);
  const params = getRouteParams(route);
  return toSignal(route.params.pipe(map(() => getRouteParams(route))), { initialValue: params });
}

export function injectRouteParam(key: string) {
  const route = inject(ActivatedRoute);
  const params = getRouteParams(route);
  return toSignal(route.params.pipe(map(() => getRouteParams(route)[key])), { initialValue: params[key] });
}

export function currentRoute(router: Router) {
  return toSignal(
    router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => router.url)
    ),
    { initialValue: router.url }
  );
}
